import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { SocketProvider } from './store/SocketContext';
import { ThemeProvider } from './store/ThemeContext';
import { AuthProvider, useAuth } from './store/AuthContext';
import { ConfirmDialogProvider } from './store/ConfirmDialogContext';
import { setUnauthorizedHandler } from './config/api';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DashboardLayout from './components/DashboardLayout';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminPage from './pages/AdminPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CouponPage from './pages/CouponPage';
import ProfanityPage from './pages/ProfanityPage';
import MyProfanityPage from './pages/MyProfanityPage';
import PollChargePage from './pages/PollChargePage';
import EventPage from './pages/EventPage';
import PresenterView from './pages/activities/PresenterView';
import JoinPage from './pages/join/JoinPage';
import ComingSoon from './pages/ComingSoon';
import TutorialsPage from './pages/TutorialsPage';
import SettingsPage from './pages/SettingsPage';
import CreditsCouponPage from './pages/CreditsCouponPage';
import NotFound from './pages/NotFound';
import TransferHistoryPage from './pages/TransferHistoryPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import EventsPage from './pages/EventsPage';
import ResponsesPage from './pages/ResponsesPage';
import EmailProvidersPage from './pages/EmailProvidersPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';

const APP_TITLE = 'LivePulse';

function DocumentTitle() {
    const { pathname } = useLocation();
    useEffect(() => {
        const segment = pathname.split('/').filter(Boolean);
        if (segment[0] === 'join') {
            document.title = segment[1] ? `Join Event | ${APP_TITLE}` : `Join | ${APP_TITLE}`;
        } else if (segment[0] === 'events' && segment[1]) {
            if (segment[2] === 'present') document.title = `Present | ${APP_TITLE}`;
            else document.title = `Event | ${APP_TITLE}`;
        } else {
            const titles = {
                '': `${APP_TITLE} — Real-Time Engagement`,
                login: `Sign in | ${APP_TITLE}`,
                signup: `Sign up | ${APP_TITLE}`,
                'forgot-password': `Reset password | ${APP_TITLE}`,
                'reset-password': `Set new password | ${APP_TITLE}`,
                'verify-email': `Verify email | ${APP_TITLE}`,
                dashboard: `Dashboard | ${APP_TITLE}`,
                events: `Events | ${APP_TITLE}`,
                responses: `Manage Responses | ${APP_TITLE}`,
                analytics: `Analytics | ${APP_TITLE}`,
                admin: `Admin | ${APP_TITLE}`,
                'email-providers': `Email providers | ${APP_TITLE}`,
                coupons: `Coupons | ${APP_TITLE}`,
                profanity: `Profanity filter | ${APP_TITLE}`,
                'my-blocked-words': `My blocked words | ${APP_TITLE}`,
                'poll-charge': `Pricing | ${APP_TITLE}`,
                team: `Team | ${APP_TITLE}`,
                'transfer-history': `Transfer History | ${APP_TITLE}`,
                tutorials: `Tutorials | ${APP_TITLE}`,
                settings: `Settings | ${APP_TITLE}`,
                credits: `Credits & Coupon | ${APP_TITLE}`,
                about: `About | ${APP_TITLE}`,
                terms: `Terms of Service | ${APP_TITLE}`,
                privacy: `Privacy Policy | ${APP_TITLE}`,
            };
            document.title = titles[segment[0] || ''] || `${APP_TITLE}`;
        }
    }, [pathname]);
    return null;
}

function AuthHandler() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        setUnauthorizedHandler(() => {
            logout();
            navigate('/login', { replace: true });
        });
        return () => setUnauthorizedHandler(() => {});
    }, [logout, navigate]);
    return null;
}

function App() {
    return (
        <ThemeProvider>
            <SocketProvider>
                <ConfirmDialogProvider>
                    <Router>
                        <AuthProvider>
                            <AuthHandler />
                            <DocumentTitle />
                            <Routes>
                                <Route path="/" element={<LandingPage />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/signup" element={<SignUp />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route path="/verify-email" element={<VerifyEmailPage />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/terms" element={<TermsPage />} />
                                <Route path="/privacy" element={<PrivacyPage />} />
                                <Route path="/join/:eventCode?" element={<JoinPage />} />
                                <Route path="/events/:eventId/present" element={<ProtectedRoute><PresenterView /></ProtectedRoute>} />
                                <Route
                                    element={
                                        <ProtectedRoute>
                                            <DashboardLayout />
                                        </ProtectedRoute>
                                    }
                                >
                                    <Route path="/dashboard" element={<Dashboard />} />
                                    <Route path="/events" element={<EventsPage />} />
                                    <Route path="/responses" element={<ResponsesPage />} />
                                    <Route path="/transfer-history" element={<TransferHistoryPage />} />
                                    <Route path="/analytics" element={<AnalyticsPage />} />
                                    <Route path="/admin" element={<AdminPage />} />
                                    <Route path="/email-providers" element={<EmailProvidersPage />} />
                                    <Route path="/coupons" element={<CouponPage />} />
                                    <Route path="/profanity" element={<ProfanityPage />} />
                                    <Route path="/my-blocked-words" element={<MyProfanityPage />} />
                                    <Route path="/poll-charge" element={<PollChargePage />} />
                                    <Route path="/integrations" element={<IntegrationsPage />} />
                                    <Route path="/events/:eventId" element={<EventPage />} />
                                    <Route
                                        path="/team"
                                        element={
                                            <ComingSoon
                                                title="Team"
                                                description="Collaborate with your team, manage roles, and share events — team features are on their way!"
                                            />
                                        }
                                    />
                                    <Route
                                        path="/tutorials"
                                        element={<TutorialsPage />}
                                    />
                                    <Route
                                        path="/settings"
                                        element={<SettingsPage />}
                                    />
                                    <Route
                                        path="/credits"
                                        element={<CreditsCouponPage />}
                                    />
                                </Route>
                        <Route path="*" element={<NotFound />} />
                            </Routes>
                        </AuthProvider>
                    </Router>
                </ConfirmDialogProvider>
            </SocketProvider>
        </ThemeProvider>
    );
}

export default App;
